import { Box, Chip } from "@mui/material";
import { alertPageStyles } from "../../styles/AlertsPage.styles";
import { getFDSStockData, processLegsWithFDSData } from "../../utils/fdsUtils";
import {
  actionCallChipSx,
  actionPutChipSx,
  greenMoneyChipSx,
  grayChipSx,
} from "../../styles/RedirectionModal.styles";

interface AlertDescriptionProps {
  payload: {
    body: string;
  };
  symbol?: string;
  contract_type?: string;
  net_avg_price?: number;
  auto_extension_percentage?: number | null;
  auto_partial_close_percentage?: number | null;
  legs?: Array<{
    action: string;
    strike_price: number;
    expiration_date: string;
  }>;
  type: string;
  FDS_stockData: any;
  FDS_optionData: any;
}

/**
 * Component for displaying alert description with real-time FDS data
 * Shows stock prices, option prices, and delta values for trade signals
 */
export const AlertDescription = ({
  payload,
  symbol,
  contract_type,
  net_avg_price,
  auto_extension_percentage,
  auto_partial_close_percentage,
  legs,
  type,
  FDS_stockData,
  FDS_optionData,
}: AlertDescriptionProps) => {
  const stockData = getFDSStockData(symbol || "", FDS_stockData);
  const legsWithFDSData = processLegsWithFDSData(
    legs || [],
    symbol || "",
    FDS_optionData,
  );

  return (
    <Box sx={alertPageStyles.descriptionContainer}>
      <Box sx={alertPageStyles.descriptionText}>{payload.body}</Box>
      {(symbol ||
        contract_type ||
        net_avg_price ||
        (legs && legs.length > 0)) && (
        <Box sx={alertPageStyles.metadataContainer}>
          {contract_type && (
            <Box sx={alertPageStyles.fieldRow}>
              <Box sx={alertPageStyles.label}>Action:</Box>
              <Chip
                label={contract_type === "C" ? "CALL" : "PUT"}
                size="small"
                sx={contract_type === "C" ? actionCallChipSx : actionPutChipSx}
              />
            </Box>
          )}
          {symbol && (
            <Box sx={alertPageStyles.fieldRow}>
              <Box sx={alertPageStyles.label}>Symbol:</Box>
              <Chip label={symbol} size="small" sx={grayChipSx} />
            </Box>
          )}
          {type === "Trade Signal" && stockData.lastPrice !== "N/A" && (
            <Box sx={alertPageStyles.fieldRow}>
              <Box sx={alertPageStyles.label}>Last Price:</Box>
              <Chip
                label={`$${parseFloat(stockData.lastPrice).toFixed(2)}`}
                size="small"
                sx={greenMoneyChipSx}
              />
            </Box>
          )}
          {net_avg_price && (
            <Box sx={alertPageStyles.fieldRow}>
              <Box sx={alertPageStyles.label}>Net Avg Price:</Box>
              <Chip
                label={`$${net_avg_price}`}
                size="small"
                sx={greenMoneyChipSx}
              />
            </Box>
          )}
          {auto_extension_percentage !== null &&
            auto_extension_percentage !== undefined && (
              <Box sx={alertPageStyles.fieldRow}>
                <Box sx={alertPageStyles.label}>Auto Extension:</Box>
                <Chip
                  label={`${auto_extension_percentage}%`}
                  size="small"
                  sx={grayChipSx}
                />
              </Box>
            )}
          {auto_partial_close_percentage !== null &&
            auto_partial_close_percentage !== undefined && (
              <Box sx={alertPageStyles.fieldRow}>
                <Box sx={alertPageStyles.label}>Auto Partial Close:</Box>
                <Chip
                  label={`${auto_partial_close_percentage}%`}
                  size="small"
                  sx={grayChipSx}
                />
              </Box>
            )}
          {legsWithFDSData && legsWithFDSData.length > 0 && (
            <Box>
              <Box sx={alertPageStyles.legsContainer}>Legs:</Box>
              {legsWithFDSData.map(
                (
                  leg: {
                    action: string;
                    strike_price: number;
                    expiration_date: string;
                    optionSymbol?: string;
                    optionLastPrice?: string;
                  },
                  index: number,
                ) => {
                  const upperAction = (leg.action || "").toUpperCase();
                  const isBuy = upperAction === "BUY";
                  const isSell = upperAction === "SELL";
                  const chipSx = {
                    bgcolor: isBuy
                      ? "#00ff0040"
                      : isSell
                        ? "#fb923c"
                        : "#e5e7eb",
                    color: "#000000",
                    fontWeight: 700,
                    fontSize: 12,
                  } as const;
                  return (
                    <Box key={index} sx={alertPageStyles.legItem}>
                      <Chip label={upperAction} size="small" sx={chipSx} />
                      <Box sx={alertPageStyles.legDate}>
                        {leg.expiration_date}
                      </Box>
                      <Box sx={alertPageStyles.legSeparator}>@</Box>
                      <Box sx={alertPageStyles.legStrikePrice}>
                        ${leg.strike_price}
                      </Box>
                    </Box>
                  );
                },
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
